import React, { useState, useEffect } from "react";
import _ from "lodash";
import { BsDownload } from "react-icons/bs";
import { useLazyGetDataWithinRangeQuery } from "../../../services/dataAPI";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import DateRangePicker from "../../../utils/DateRangePicker";
import { Device } from "../../../features/devices/deviceSlice";
import DownloadFormat, { DownloadFormatType } from "./DownloadFormat";
import DateFormatSelector, {
  formatUTCDate,
  formatCustomDateTime,
  FormatType,
} from "./DateFormatSelector";
import { CiCircleRemove } from "react-icons/ci";
import DataTitleFormat, { Format } from "./DataTitleFormat";
import DevicePhenomenaSelector, { LineListEntry } from "./DevicePhenomenaSelector";
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from "react-icons/md";
import AnimateHeight from "react-animate-height";

export enum DownloadType {
  PHENOMENA = "Phenomena",
  GATEWAY = "Gateway",
}

interface DownloadModalProps {
  downloadType: DownloadType;
  device: Device;
  lineList?: LineListEntry[];
  setLIneList?: (lines: LineListEntry[]) => void;
  onClose: () => void;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  downloadType,
  device,
  lineList,
  setLIneList,
  onClose,
}) => {
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormatType>(
    DownloadFormatType.CSV
  );
  const [startDate, setStartDate] = useState<Date>(
    new Date(new Date().setHours(0, 0, 0, 0))
  );
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [dateFormat, setDateFormat] = useState<FormatType>(FormatType.DMY_HMS);
  const [dataTitleFormat, setDataTitleFormat] = useState<Format>(Format.CONFIG);
  const [showAdvanceOption, setShowAdvanceOption] = useState<Boolean>(false);

  const handleDateRangeChange = (newStartDate: Date | null, newEndDate: Date | null) => {
    if (newStartDate && newEndDate) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  };

  const phenoFilter = (data: any, filter: any[], titleFormat: Format) => {
    const checkedConfigs = filter
      .filter((f) => f.checked)
      .map((f) => ({ config: f.config, pheno: f.pheno, name: f.name }));

    return data.dashboard_data_points[0].data.map((entry: any) => {
      const result: any = { ts: formatCustomDateTime(entry.ts, dateFormat) };

      checkedConfigs.forEach(({ config, pheno, name }) => {
        const configData = entry.data_points[config];
        if (configData && configData.hasOwnProperty(pheno)) {
          titleFormat === Format.CONFIG
            ? (result[pheno] = configData[pheno])
            : (result[name] = configData[pheno]);
        }
      });

      return result;
    });
  };
  const gateWayFilter = (data: any) => {
    const formattedData: any = [];

    data.dashboard_data_points.forEach((point: any) => {
      point.data.forEach((item: any) => {
        const ts = item.ts;

        Object.keys(item.data_points.rssi).forEach((rssiKey) => {
          const rssiValue = item.data_points.rssi[rssiKey];

          if (item.data_points.snr[rssiKey] !== undefined) {
            const snrValue = item.data_points.snr[rssiKey];

            formattedData.push({
              ts: formatCustomDateTime(ts, dateFormat),
              rssi: rssiValue,
              snr: snrValue,
              gatewayId: rssiKey,
            });
          }
        });
      });
    });

    return formattedData;
  };

  const downloadAsJson = (
    data: Record<string, any>[] | Record<string, any>,
    startDate: Date | string,
    endDate: Date | string
  ) => {
    const {
      day: startDay,
      month: startMonth,
      year: startYear,
    } = formatUTCDate(startDate);
    const { day: endDay, month: endMonth, year: endYear } = formatUTCDate(endDate);
    const renameKeys = (
      obj: Record<string, any>,
      keyMap: Record<string, string>
    ): Record<string, any> => {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [keyMap[key] || key, value])
      );
    };

    const keyMap: Record<string, string> = {
      ts: "Timestamp (GMT)",
      rssi: "Signal Strength (RSSI)",
      snr: "Signal Noise Ratio (SNR)",
      gatewayId: "Gateway ID",
    };

    const renamedData = Array.isArray(data)
      ? data.map((item) => renameKeys(item, keyMap))
      : renameKeys(data, keyMap);

    const json = JSON.stringify(renamedData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${device.name}_${startDay}-${startMonth}-${startYear}_${endDay}-${endMonth}-${endYear}}.json`;

    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAsCsv = (
    data: Record<string, any>[],
    startDate: Date | string,
    endDate: Date | string
  ) => {
    if (!data.length) return;

    const {
      day: startDay,
      month: startMonth,
      year: startYear,
    } = formatUTCDate(startDate);

    const { day: endDay, month: endMonth, year: endYear } = formatUTCDate(endDate);

    const columnMap: Record<string, string> = {
      ts: "Timestamp (GMT)",
      rssi: "Signal Strength (RSSI)",
      snr: "Signal Noise Ratio (SNR)",
      gatewayId: "Gateway ID",
    };

    const allKeys = Array.from(new Set(data.flatMap((item) => Object.keys(item))));

    // Create CSV header row with renamed keys (if present in columnMap)
    const headerRow = allKeys.map((key) => columnMap[key] || key).join(",");

    const csvRows = [
      headerRow,
      ...data.map((row) =>
        allKeys.map((key) => `"${String(row[key] ?? "").replace(/"/g, '""')}"`).join(",")
      ),
    ];

    const csv = csvRows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${device.name}_${startDay}-${startMonth}-${startYear}_${endDay}-${endMonth}-${endYear}}.csv`;
    a.click();

    URL.revokeObjectURL(url);
  };

  const [
    getDataWithinRange,
    {
      data: dataWithinRange,
      isSuccess: isGetDataWithinRangeSuccess,
      isError: isGetDataWithinRangeError,
      error: getDataWithinRangeError,
      isFetching: isGetDataWithinRangeFetching,
    },
  ] = useLazyGetDataWithinRangeQuery();

  const handleDownload = async () => {
    if (device) {
      const { data } = await getDataWithinRange({
        eui: [device?.eui],
        start: startDate.getTime(),
        end: endDate.getTime(),
      });
      if (data) {
        const download =
          downloadType === DownloadType.PHENOMENA
            ? phenoFilter(data, lineList || [], dataTitleFormat)
            : gateWayFilter(data);
        downloadFormat === DownloadFormatType.JSON
          ? downloadAsJson(download, data.start_time, data.end_time)
          : downloadAsCsv(download, data.start_time, data.end_time);
      }
    }
  };

  useEffect(() => {
    if (isGetDataWithinRangeSuccess) {
      setTimeout(() => onClose(), 1000);
    } else if (isGetDataWithinRangeError) {
      toast.error("Something went wrong. Try again later");
      setTimeout(() => onClose(), 1000);
    }
  }, [isGetDataWithinRangeSuccess, isGetDataWithinRangeError, dataWithinRange, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 px-4 pt-10">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          title="Close"
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 focus:outline-none cursor-pointer"
        >
          <CiCircleRemove size={30} color="grey-500" />
        </button>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
            {device?.name}
          </h2>

          <DownloadFormat format={downloadFormat} onChange={setDownloadFormat} />
          {downloadType === DownloadType.PHENOMENA && lineList && setLIneList && (
            <DevicePhenomenaSelector lineList={lineList} setLineList={setLIneList} />
          )}

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateRangeChange}
          />
          <button
            className="w-full text-left"
            type="button"
            onClick={() => setShowAdvanceOption((v) => !v)}
          >
            <div className="bg-gray-200 rounded-lg w-fit">
              <div className="text-black font-semibold px-2 inline-flex space-x-1">
                <div>Advance Option</div>
                <div className="pt-[3px]">
                  {showAdvanceOption ? (
                    <MdKeyboardArrowDown size={18} />
                  ) : (
                    <MdKeyboardArrowRight size={18} />
                  )}
                </div>
              </div>
            </div>
          </button>

          <AnimateHeight
            duration={200}
            height={showAdvanceOption ? "auto" : 0}
            className="rounded-lg ml-1"
          >
            <div className="mt-1 mb-2 text-md text-gray-600 flex justify-between w-full">
              <div className="w-[45%]">
                <DateFormatSelector
                  selectedFormat={dateFormat}
                  onChange={setDateFormat}
                />
              </div>
              {downloadType === DownloadType.PHENOMENA && (
                <div className="w-[45%]">
                  <DataTitleFormat
                    titleFormat={dataTitleFormat}
                    onChange={setDataTitleFormat}
                  />
                </div>
              )}
            </div>
          </AnimateHeight>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm sm:text-base"
              disabled={isGetDataWithinRangeFetching}
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={isGetDataWithinRangeFetching}
              className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center min-w-[100px] text-sm sm:text-base ${
                isGetDataWithinRangeFetching ? "cursor-not-allowed opacity-75" : ""
              }`}
            >
              {isGetDataWithinRangeFetching ? (
                <FaSpinner className="animate-spin h-5 w-5" />
              ) : (
                <BsDownload size={24} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
